import {priority} from '@priority-software/priority-proto-api/lib';
import {formatDate} from '@priority-software/proto-business-logic/lib/utils/date';
import {Operator} from '../../../../_legacy/Searches/consts/operators';
import {serializeFilterValues} from '../../common/serializeFilterValues';
import {ApiOperatorMapping, TextApiOperatorMapping} from '../../../AppSearches/types/operator';
import {ISearchCondition} from '../../../AppSearches/types/filters';
import {isRTL} from '../../../../Localization';
import {getDateRangesMap} from '../../../../common/dateRange';
import {ColumnType, getBaseColumnType} from '../../../AppSearches/types/column';
import {Filter} from '../../../../stores/Form/Filter';

import {SavedSearch} from '../../types';
import {PatchFilter} from '../../components/AdvancedSearch/AdvancedSearchDrawer';
import {convertTimeFromUSA} from './time';
import {ICondition} from '../../../../stores/Form/Condition';
import {Form} from '../../../../stores/Form/Form';
import {getUuid4} from '../../../../utils/getUuid4';

export const patchConditionOperator = (
    operator: Operator
): priority.netitems.shared.ComparisonOperator => {
    if (
        operator === Operator.Equals ||
        operator === Operator.Contains ||
        operator === Operator.StartsWith ||
        operator === Operator.EndsWith
    ) {
        return priority.netitems.shared.ComparisonOperator.Eq;
    }
    return TextApiOperatorMapping[operator];
};

export const createFilter = (
    conditions: ISearchCondition[],
    filterOperator: priority.netitems.shared.LogicalOperator = priority.netitems.shared
        .LogicalOperator.Or
) => {
    const patchCondition = conditions.map(condition => ({
        ColumnName: condition.columnName,
        ColumnID: condition.columnID,
        Operator:
            condition.type === ColumnType.Text
                ? patchConditionOperator(condition.operator)
                : ApiOperatorMapping[condition.operator],
        Values: serializeFilterValues(condition.operator, condition.values),
        DateEnum: condition.dateEnum,
        IgnoreCase: condition.ignoreCase
    }));

    return new Filter({Condition: patchCondition, Operator: filterOperator});
};

export const filteredConditions = (conditions: ISearchCondition[]) => {
    const newConditions = conditions.map(cond => {
        const condition =
            !cond.values.length &&
            cond.operator !== Operator.Empty &&
            cond.operator !== Operator.NotEmpty;

        return condition ? null : cond;
    });
    return newConditions.filter(cond => cond !== null);
};

export const removeFiltersWithoutValue = (
    filters: PatchFilter[] = []
): PatchFilter[] | undefined => {
    const checkedFilters = filters.reduce((acc: PatchFilter[], filter) => {
        const filteredCondition = filteredConditions(filter.Condition);
        if (filteredCondition.length) {
            acc.push({...filter, Condition: filteredCondition});
        }

        return acc;
    }, []);

    return checkedFilters.length ? checkedFilters : undefined;
};

export const patchFilterToStore = (filter: PatchFilter) => {
    const newConditions = filter.Condition.map(condition => {
        if (condition.type === ColumnType.Date) {
            return convertConditionWithDate(condition);
        }
        if (condition.type === ColumnType.Time) {
            return convertConditionWithTime(condition);
        }
        if (condition.type === ColumnType.Bool && condition.values[0] === 'N') {
            return {...condition, values: ['']};
        }
        return condition;
    });

    return createFilter(newConditions, filter.Operator);
};

// convertStrToDate get argument from datePicker .it can be ISOstring or string like :"today"
export const convertStrToDate = (value: string): Date | undefined => {
    const dateRangeMap = getDateRangesMap();
    return dateRangeMap[value]?.getDate();
};

const wildcard = /[*?]/;
export const convertConditionWithDate = (condition: ISearchCondition): ISearchCondition => {
    const values = condition.values.map(value => {
        if (!wildcard.test(value) && value) {
            const formattedValue = convertStrToDate(value) || new Date(value);
            return formatDate(formattedValue, {
                isRTL: isRTL(),
                format: 'date'
            });
        }
        return value;
    });

    return {
        ...condition,
        values
    };
};

export const convertConditionWithTime = (condition: ISearchCondition): ISearchCondition => {
    const newValues = condition.values.map(value =>
        value.includes('M') ? convertTimeFromUSA(value) : value
    );

    return {
        ...condition,
        values: newValues
    };
};

const prepareSavedSearchFilters = (filters: Array<Filter>): Array<Filter> => {
    return filters
        .map(f => {
            /*
             * reset values for date range if it uses predefined date value from date picker
             * the actual dates value will be set on the search execution method + mapped in the UI component (DateControl)
             */
            return {
                ...f,
                Condition: f.Condition.map(c =>
                    c.DateEnum?.length > 0 ? {...c, Values: []} : c
                ).filter(
                    // validate if the search has valid conditions
                    (c: ICondition) =>
                        c.Operator === priority.netitems.shared.ComparisonOperator.IsNull ||
                        c.Operator === priority.netitems.shared.ComparisonOperator.IsNotNull ||
                        c.DateEnum?.length > 0 ||
                        c.Values?.length > 0
                )
            };
        })
        .filter(f => f?.Condition?.length > 0);
};

export const serializeSearches = (searches: SavedSearch[]): SavedSearch[] => {
    return searches.map(search => {
        /*
         * Todo: it's a temporarily fix of the corrupt saved search object. Sometimes saved search object doesn't have
         *  `filters: Array<Filter>` field but instead has `filter: Filter` field
         */
        const filters: Array<Filter> = prepareSavedSearchFilters(
            search.filters || [(search as any).filter]
        );

        return {
            ...search,
            filters,
            isDefault: Boolean(search.isDefault),
            index: Math.abs(search.index),
            id: search?.id || getUuid4()
        };
    });
};

export const desrializeSearches = (searches: SavedSearch[], isSaved: boolean = true) => {
    return searches.map(search => ({
        ...search,
        index: -Math.abs(search.index),
        isDefault: search.isDefault ? 1 : 0,
        isSaved
    }));
};
export const findAvailableIndex = (searches: SavedSearch[]): number => {
    const sorted = [...searches].sort((a, b) => a.index - b.index);
    for (let i = 0; i < sorted.length; i++) {
        if (sorted[i].index !== i + 1) {
            return i + 1;
        }
    }
    return sorted.length + 1;
};

export const prepareFilterToServer = (form: Form, filters: Filter[]): Filter[] => {
    const newFilters = filters.reduce((acc: Filter[], filter: Filter) => {
        if (
            filter.Condition.length === 2 &&
            filter.Operator === priority.netitems.shared.LogicalOperator.And
        ) {
            filter.Condition.forEach(cond => {
                acc.push({
                    ...filter,
                    Condition: [cond],
                    Operator: priority.netitems.shared.LogicalOperator.Or
                });
            });
            return acc;
        }
        acc.push(filter);
        return acc;
    }, []);

    return newFilters.map(filter => {
        const column = form.getColumnByID(filter.Condition[0].ColumnID as number);
        const isTextColumn = getBaseColumnType(column?.Type) === ColumnType.Text;
        const conditions = filter.Condition.reduce((acc: ICondition[], condition: ICondition) => {
            if (
                condition.Operator === priority.netitems.shared.ComparisonOperator.Eq &&
                isTextColumn &&
                condition.Values.length > 1
            ) {
                condition.Values.forEach(value => {
                    acc.push({
                        ...condition,
                        Values: [value]
                    });
                });
                return acc;
            }
            acc.push(condition);
            return acc;
        }, []);
        return {...filter, Condition: conditions};
    });
};
